import React from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Button, Tooltip, Dropdown, Modal, Input, Tag, Empty, List, Avatar, Select, Form, Row, Col, Space, Upload, Tabs, message, Typography } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  CloseOutlined,
  TeamOutlined,
  UserOutlined,
  FileTextOutlined,
  GlobalOutlined,
  UploadOutlined,
  SaveOutlined,
  RobotOutlined,
  RocketOutlined,
  SearchOutlined,
  MoreOutlined
} from '@ant-design/icons';
import TiptapEditor from '../TiptapEditor/TiptapEditor';
import UserMenu from '../UserMenu';
import { useCollaborativeKnowledgeBase } from '../../contexts/CollaborativeKnowledgeBaseContext';
import { usePrivateKnowledgeBase } from '../../contexts/PrivateKnowledgeBaseContext';
import { useEffect, useState, useCallback, useRef } from 'react';
import apiClient from '../../services/api';
import { topicTreeService } from '../../services/topicTreeService';
import {
  getSelectedFolder,
  getSelectedKB,
  addGlobalStateListener,
  setSelectedFolder,
  setSelectedTopic,
  setSelectedKB,

  setSelectedTag,
  setSearchKeyword
} from '../../hooks/useDashboardGlobalVar';
import useDoubleClickProtection from '../../hooks/useDoubleClickProtection';
import { useNoteTaskEvents } from '../../hooks/useNoteTaskEvents';
import NewNoteModal from './NewNoteModal';

const { Sider } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Text } = Typography;

const DashboardSidebar = ({
  // Props for basic sidebar
  leftmostSiderWidth,
  user,
  quickActions,
  handleOpenCreateModal,
  handleQuickActionClick,

  // Props for private topics
  privateTopics,
  expandedTopics,
  hasPrivateKB,
  editingTopic,
  editingTopicName,
  onTopicClick,
  onToggleExpanded,
  onEditTopic,
  onConfirmEditTopic,
  onCancelEditTopic,
  renderTopicTree,

  // Props for collaborative KBs
  collaborativeKBs,
  expandedCollaborativeKBsArray,
  onToggleCollaborativeKBExpanded,
  onCollaborativeKBClick,
  onStartCreateCollaborativeKBTopic,
  onRenameCollaborativeKB,
  onConfirmRenameCollaborativeKB,
  onCancelRenameCollaborativeKB,
  onShareCollaborativeKB,
  onViewMembers,
  onSetIsCreateCollaborativeVisible,
  calculateCollaborativeKBNotesCount,
  calculateTotalCollaborativeKBNotesCount,
  onLeaveCollaborativeKB,

  // Props for stats
  notesStats,
  calculatePrivateKBNotesCount,

  // Props for new note creation modals
  createModalEnableAIWriter = false,
  isCreateModalVisible,
  isCreatingNote,
  isBatchCreating,
  batchResult,
  selectedFiles,
  createNoteKnowledgeBase,
  createAvailableKnowledgeBases,
  manualInputContent,
  createTimer,
  isCreateTopicVisible,
  isCreateSubTopicVisible,
  selectedParentTopic,
  isEditTopicVisible,
  editingTopicContext,
  createForm,
  linkForm,
  createTopicForm,
  createSubTopicForm,
  editTopicForm,
  handleCreateModalCancel,
  handleCreateNote,
  handleCreateFromLink,
  handleCreateNoteKnowledgeBaseChange,
  setManualInputContent,
  handleCreateTopic,
  handleCancelCreateTopic,
  handleCreateSubTopic,
  handleCancelCreateSubTopic,
  handleConfirmEditTopic,
  handleCancelEditTopic,
  setIsCreateTopicVisible,
  handleCreateCollaborative,
  isCreateCollaborativeVisible,
  setIsCreateCollaborativeVisible,
  setSelectedFiles,
  setIsCreateModalVisible,
  setIsBatchCreating,
  setIsCreatingNote
}) => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  // 添加状态来强制重新渲染
  const [forceUpdate, setForceUpdate] = useState(0);

  // Footer 消息状态
  const [footerMessage, setFooterMessage] = useState(null);

  // 防双击保护Hook
  const createTopicProtection = useDoubleClickProtection(2000);
  const createSubTopicProtection = useDoubleClickProtection(2000);
  const createCollaborativeProtection = useDoubleClickProtection(2000);
  const createNoteProtection = useDoubleClickProtection(60000); // 创建笔记保护，60秒


  // SSE 事件处理：任务完成
  const handleTaskCompleted = useCallback((event) => {
    console.log('[DashboardSidebar] Task completed:', event.noteId);

    // Post message to footer
    const timestamp = new Date().toLocaleTimeString();
    setFooterMessage({
      type: 'success',
      text: t('messages:note_ai_processing_complete', { defaultValue: 'AI task completed' }),
      timestamp
    });
  }, [t]);

  // SSE 事件处理：任务失败
  const handleTaskFailed = useCallback((event) => {
    console.log('[DashboardSidebar] Task failed for note:', event.noteId, event.error);
    message.error(t('messages:note_ai_processing_failed', { defaultValue: 'AI processing failed for note' }));

    // // Post error message to footer
    // const timestamp = new Date().toLocaleTimeString();
    // setFooterMessage({
    //   type: 'error',
    //   text: t('messages:note_ai_processing_failed', { defaultValue: 'AI processing failed' }),
    //   timestamp
    // });

    // // Auto-clear footer message after 10 seconds
    // setTimeout(() => {
    //   setFooterMessage(null);
    // }, 10000);
  }, [t]);

  // 初始化 SSE 连接，订阅任务事件
  const { isConnected: isSSEConnected, connectionError: sseConnectionError } = useNoteTaskEvents({
    onTaskCompleted: handleTaskCompleted,
    onTaskFailed: handleTaskFailed,
  });

  // 记录 SSE 连接状态
  useEffect(() => {
    if (isSSEConnected) {
      console.log('[DashboardSidebar] SSE connection established for task events');
    } else if (sseConnectionError) {
      console.warn('[DashboardSidebar] SSE connection error:', sseConnectionError);
    }
  }, [isSSEConnected, sseConnectionError]);

  // 同步表单值到 Ant Design 表单系统
  useEffect(() => {
    if (createForm && manualInputContent !== undefined) {
      createForm.setFieldsValue({ content: manualInputContent });
    }
  }, [manualInputContent, createForm]);

  useEffect(() => {
    if (createForm && createNoteKnowledgeBase?.kb_id) {
      createForm.setFieldsValue({ knowledge_base: createNoteKnowledgeBase.kb_id });
    }
  }, [createNoteKnowledgeBase, createForm]);

  // 监听 expandedTopics 变化，强制重新渲染
  useEffect(() => {
    console.log('[DashboardSidebar] expandedTopics 变化，强制重新渲染:', expandedTopics);
    setForceUpdate(prev => prev + 1);
  }, [expandedTopics]);

  // 监听 expandedCollaborativeKBsArray 变化，强制重新渲染
  useEffect(() => {
    console.log('[DashboardSidebar] expandedCollaborativeKBsArray 变化，强制重新渲染:', expandedCollaborativeKBsArray);
    setForceUpdate(prev => prev + 1);
  }, [expandedCollaborativeKBsArray]);

  // 监听全局状态变化，强制重新渲染以确保快捷操作加亮正确
  useEffect(() => {
    // 使用全局状态监听器替代 setInterval
    const removeListener = addGlobalStateListener((newState, oldState, updates) => {
      // 检查是否有相关的状态变化
      if (updates.selectedFolder || updates.selectedTopic || updates.selectedKB) {
        console.log('[DashboardSidebar] 检测到全局状态变化，强制重新渲染:', {
          updates,
          oldState: {
            selectedFolder: oldState.selectedFolder,
            selectedTopic: oldState.selectedTopic,
            selectedKB: oldState.selectedKB
          },
          newState: {
            selectedFolder: newState.selectedFolder,
            selectedTopic: newState.selectedTopic,
            selectedKB: newState.selectedKB
          }
        });

        // 强制重新渲染
        setForceUpdate(prev => prev + 1);
      }
    });

    // 清理监听器
    return removeListener;
  }, []);
  // 从协作库context获取数据
  const {
    collaborativeKBTopics,
    isMembersVisible,
    selectedKBForMembers,
    kbMembers,
    loadingMembers,
    setIsMembersVisible,
    isCollaborativeKBCreator
  } = useCollaborativeKnowledgeBase();

  // 从私有知识库context获取数据
  const { handleInitializePrivateKB } = usePrivateKnowledgeBase();

  useEffect(() => {
    if (isEditTopicVisible) {
      editTopicForm.setFieldsValue({
        name: editingTopic?.name,
        description: editingTopic?.description,
        tagnamelist: editingTopic?.tagnamelist
      });
    }
  }, [isEditTopicVisible, editingTopic, editTopicForm]);

  // AI生成标签相关状态
  const [aiGenerating, setAiGenerating] = useState(false);

  // 点击保护状态（已废弃，改用 createNoteProtection）
  // const [isCreateButtonClicked, setIsCreateButtonClicked] = useState(false);

  // AI生成标签函数
  const handleAIGenerateTags = async (formInstance) => {
    try {
      setAiGenerating(true);

      // 获取当前表单的主题名称和描述
      const currentValues = formInstance.getFieldsValue(['name', 'description']);

      if (!currentValues.name || !currentValues.description) {
        message.warning(t('messages:please_fill_topic_info', { defaultValue: 'Please fill in topic name and description for AI tag recommendation' }));
        return;
      }

      // 调用AI标签推荐服务
      const response = await topicTreeService.recommendTopicTags({
        topicName: currentValues.name,
        description: currentValues.description
      });

      console.log('AI推荐标签响应:', response);
      console.log('响应数据结构:', {
        responseData: response.data,
        responseDataData: response.data?.data,
        tags: response.data?.data?.tags
      });

      // 自动填充推荐的标签
      const tags = response.data?.data?.tags;
      if (tags) {
        console.log('准备设置表单值:', { tagnamelist: tags });

        // 先设置表单值
        formInstance.setFieldsValue({
          tagnamelist: tags
        });

        // 强制触发组件重新渲染
        setForceUpdate(prev => prev + 1);

        // 使用setTimeout确保UI更新
        setTimeout(() => {
          // 强制触发表单重新渲染
          formInstance.validateFields(['tagnamelist']).then(() => {
            console.log('表单验证成功，UI应该已更新');
          }).catch(() => {
            console.log('表单验证失败，但值已设置');
          });

          // 再次检查表单值
          const formValues = formInstance.getFieldsValue(['tagnamelist']);
          console.log('延迟检查后的表单值:', formValues);
        }, 100);

        message.success(t('messages:ai_tag_recommendation_success', { defaultValue: 'AI tag recommendation successful!' }));
        console.log('已填充标签到表单:', tags);

        // 验证表单值是否设置成功
        const formValues = formInstance.getFieldsValue(['tagnamelist']);
        console.log('设置后的表单值:', formValues);
      } else {
        console.error('响应中没有找到tags字段:', response);
        message.error(t('messages:ai_tag_format_error', { defaultValue: 'AI returned tag format is abnormal' }));
      }
    } catch (error) {
      console.error('AI推荐标签失败:', error);
      message.error(t('messages:ai_tag_recommendation_failed', { defaultValue: 'AI tag recommendation failed: {{error}}', error: error.response?.data?.message || error.message }));
    } finally {
      setAiGenerating(false);
    }
  };

  // 调试日志：监控 notesStats 变化
  useEffect(() => {
    console.log('[DashboardSidebar] notesStats 更新:', notesStats);
  }, [notesStats]);

  return (
    <Sider
      width={leftmostSiderWidth}
      theme="light"
      style={{
        background: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        borderRadius: '0',
        overflow: 'hidden'
      }}
    >
      <div className="sidebar-content">
        {/* Company Logo/Name */}
        <a
          href="https://www.vnotes.ai"
          style={{
            padding: '16px 16px 12px 16px',
            borderBottom: '1px solid #e5e7eb',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            textDecoration: 'none',
            cursor: 'pointer'
          }}
        >
          <img
            src="/vnotes-logo.png"
            alt="vnotes.ai"
            style={{ width: '24px', height: '24px' }}
          />
          <span style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#667eea',
            letterSpacing: '-0.02em'
          }}>
            VNotes.ai
          </span>
        </a>

        <div className="folder-list">
          {/* Playground Section - 已注释掉 */}
          {/* <div
            className="folder-item"
            onClick={() => {
              console.log('[DashboardSidebar] Switching to Playground');
              setSelectedFolder('playground');
              setSelectedTag(null);
              setSearchKeyword('');
            }}
            style={{
              marginBottom: '16px',
              marginTop: '4px',
              cursor: 'pointer',
              // Fake search box style
              background: getSelectedFolder() === 'playground' ? '#e6f7ff' : '#ffffff',
              border: getSelectedFolder() === 'playground' ? '1px solid #40a9ff' : '1px solid #e0e0e0',
              borderRadius: '6px',
              padding: '8px 12px',
              transition: 'all 0.3s ease',
              marginRight: '12px',
              marginLeft: '12px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e6f7ff';
              e.currentTarget.style.borderColor = '#40a9ff';
            }}
            onMouseLeave={(e) => {
              if (getSelectedFolder() === 'playground') {
                e.currentTarget.style.background = '#e6f7ff';
                e.currentTarget.style.borderColor = '#40a9ff';
              } else {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }
            }}
          >
            <div className="folder-content" style={{ gap: '10px' }}>
              <div className="folder-icon" style={{ display: 'flex', alignItems: 'center' }}>
                <SearchOutlined style={{ fontSize: '16px', color: '#666666' }} />
              </div>
              <span className="folder-title" style={{ fontSize: '14px', color: '#666666', fontWeight: '400' }}>
                {t('dashboard:sidebar.search_or_ask', { defaultValue: 'Search or Ask...' })}
              </span>
            </div>
          </div> */}
          {/* Quick Actions Section */}
          <div className="folder-header">
            <Text style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('dashboard:sidebar.quick_actions', { defaultValue: 'Quick Actions' })}</Text>
          </div>

          {/* New Note Button */}
          <div style={{ padding: '0 12px', marginBottom: '8px' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateModal}
              block
              style={{
                height: '36px',
                borderRadius: '8px',
                fontWeight: 500
              }}
            >
              {t('dashboard:sidebar.new_note', { defaultValue: 'New Note' })}
            </Button>
          </div>

          {quickActions.map(action => {
            const currentSelectedFolder = getSelectedFolder();
            const isActive = currentSelectedFolder === action.key;

            //console.log('[DashboardSidebar] 渲染快捷操作:', {
            //actionKey: action.key,
            //currentSelectedFolder,
            //isActive,
            //action
            //});

            return (
              <div
                key={action.key}
                className={`folder-item ${isActive ? 'active' : ''}`}
                onClick={(e) => {
                  console.log('[DashboardSidebar] 快捷操作点击:', action.key, action);
                  e.preventDefault();
                  e.stopPropagation();
                  if (action.onClick) {
                    action.onClick();
                  } else {
                    console.error('[DashboardSidebar] 快捷操作缺少 onClick 函数:', action);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="folder-content">
                  <div className="folder-icon">
                    {action.icon}
                  </div>
                  <span className="folder-title">{action.title}</span>
                  <span className="folder-count">({action.count})</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Separator between Quick Actions and Personal Library */}
        <div style={{
          height: '1px',
          backgroundColor: '#e5e7eb',
          margin: '8px 16px 16px 16px'
        }} />

        {/* Private Knowledge Base Section */}
        <div className="private-section">
          <div className="section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div
                className={`folder-item ${getSelectedFolder() === 'private' ? 'active' : ''}`}
                style={{ display: 'flex', alignItems: 'center', textAlign: 'left', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', margin: '0 -8px' }}
                onClick={() => {
                  console.log('[DashboardSidebar] 点击 Personal Library 标题，显示所有私有笔记');
                  if (handleQuickActionClick) {
                    handleQuickActionClick({ key: 'private' });
                  }
                }}
                title={t('dashboard:click_to_view_all_private_notes', { defaultValue: 'Click to view all notes in Personal Library' })}
              >
                <span className="folder-title" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('dashboard:sidebar.private_notes', { defaultValue: 'Personal Library' })}</span>
                {hasPrivateKB && notesStats && calculatePrivateKBNotesCount && (
                  <span className="folder-count" style={{ marginLeft: '8px', fontSize: '11px' }}>
                    ({calculatePrivateKBNotesCount()})
                  </span>
                )}
              </div>
              {hasPrivateKB && (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'create-topic',
                        icon: <PlusOutlined />,
                        label: t('dashboard:actions.create_topic', { defaultValue: 'New Topic' }),
                        onClick: () => {
                          createTopicForm.setFieldsValue({
                            name: '',
                            description: '',
                            knowledgeBase: null
                          });
                          setIsCreateTopicVisible(true);
                        }
                      }
                    ]
                  }}
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{
                      padding: '2px 4px',
                      height: '20px',
                      fontSize: '12px',
                      color: '#1890ff'
                    }}
                  />
                </Dropdown>
              )}
            </div>
          </div>

          <div className="section-content">
            {hasPrivateKB && privateTopics ? (
              <div key={`private-topics-${expandedTopics?.length || 0}`}>
                {renderTopicTree(privateTopics, 0, { type: 'private', kbId: null }, expandedTopics)}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 10px', color: '#666', fontSize: '12px' }}>
                <div style={{ marginBottom: '8px' }}>{t('dashboard:sidebar.no_private_kb', { defaultValue: 'No private library yet' })}</div>
                <Button
                  type="link"
                  size="small"
                  onClick={handleInitializePrivateKB}
                  style={{ padding: '0', fontSize: '12px' }}
                >
                  {t('common:actions.create_now', { defaultValue: 'Create now' })}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Collaborative Knowledge Base Section */}
        <div className="collaboration-section">
          <div className="section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left', flexDirection: 'column', alignSelf: 'flex-start', width: '100%' }}>
                <Text style={{ lineHeight: '1em', height: '1em', display: 'block', margin: 0, padding: 0 }}></Text>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <div
                    className={`folder-item ${getSelectedFolder() === 'collaborative' ? 'active' : ''}`}
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', margin: '0 -8px' }}
                    onClick={() => {
                      console.log('[DashboardSidebar] 点击 Team Library 标题，显示所有协作笔记');
                      if (handleQuickActionClick) {
                        handleQuickActionClick({ key: 'collaborative' });
                      }
                    }}
                    title={t('dashboard:click_to_view_all_collaborative_notes', { defaultValue: 'Click to view all notes in Team Library' })}
                  >
                    <span className="folder-title" style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('dashboard:sidebar.collaborative_notes', { defaultValue: 'Team Library' })}</span>
                    {collaborativeKBs && collaborativeKBs.length > 0 && calculateTotalCollaborativeKBNotesCount && notesStats && (
                      <span className="folder-count" style={{ marginLeft: '8px', fontSize: '11px' }}>
                        ({calculateTotalCollaborativeKBNotesCount()})
                      </span>
                    )}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => onSetIsCreateCollaborativeVisible(true)}
                    style={{
                      padding: '2px 4px',
                      height: '20px',
                      fontSize: '12px',
                      color: '#1890ff',
                      cursor: 'pointer',
                      zIndex: 10,
                      marginLeft: 'auto'
                    }}
                    title={t('dashboard:actions.create_collaborative_kb', { defaultValue: 'Create collaborative KB' })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="section-content">
            {collaborativeKBs && collaborativeKBs.length > 0 ? (
              collaborativeKBs.map(kb => (
                <CollaborativeKBItem
                  key={kb.kb_id}
                  kb={kb}
                  isExpanded={expandedCollaborativeKBsArray.includes(kb.kb_id)}
                  onToggleExpanded={() => onToggleCollaborativeKBExpanded(kb.kb_id)}
                  onStartCreateCollaborativeKBTopic={() => onStartCreateCollaborativeKBTopic(kb)}
                  onShareCollaborativeKB={() => onShareCollaborativeKB(kb)}
                  onViewMembers={() => onViewMembers(kb)}
                  onRenameCollaborativeKB={() => onRenameCollaborativeKB(kb)}
                  onConfirmRenameCollaborativeKB={(newName) => onConfirmRenameCollaborativeKB(kb.kb_id, newName)}
                  onCancelRenameCollaborativeKB={onCancelRenameCollaborativeKB}
                  notesCount={calculateCollaborativeKBNotesCount ? calculateCollaborativeKBNotesCount(kb.kb_id) : 0}
                  renderTopicTree={renderTopicTree}
                  collaborativeKBTopics={collaborativeKBTopics}
                  selectedKB={getSelectedKB()}
                  onLeaveCollaborativeKB={() => onLeaveCollaborativeKB(kb)}
                  isCollaborativeKBCreator={() => isCollaborativeKBCreator(kb)}
                  onCollaborativeKBClick={onCollaborativeKBClick}
                  expandedCollaborativeKBsArray={expandedCollaborativeKBsArray}
                  expandedTopics={expandedTopics}
                />
              ))
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 10px', color: '#666', fontSize: '12px' }}>
                <div style={{ marginBottom: '8px' }}>{t('dashboard:sidebar.no_collab_kb', { defaultValue: 'No collaborative KB joined' })}</div>
                <Button
                  type="link"
                  size="small"
                  onClick={() => onSetIsCreateCollaborativeVisible(true)}
                  style={{ padding: '0', fontSize: '12px' }}
                >
                  {t('dashboard:actions.create_or_join', { defaultValue: 'Create or Join' })}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar Footer - User Menu at bottom */}
      <div className="sidebar-footer" style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        flexShrink: 0
      }}>

        {/* User Profile Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: footerMessage ? '8px' : '0'
        }}>
          <UserMenu userName={user?.username} notesStats={notesStats} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: isSSEConnected ? '#52c41a' : '#ff4d4f',
              display: 'inline-block'
            }} title={isSSEConnected ? 'SSE Connected' : 'SSE Disconnected'} />
          </div>
        </div>
        {footerMessage && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: footerMessage.type === 'success' ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 77, 79, 0.1)',
            color: footerMessage.type === 'success' ? '#52c41a' : '#ff4d4f',
            fontSize: '11px',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            <span>{footerMessage.type === 'success' ? '✓' : '✗'}</span>
            <span>{footerMessage.text}</span>
            <Text type="secondary" style={{ fontSize: '10px', marginLeft: '4px' }}>
              {footerMessage.timestamp}
            </Text>
          </div>
        )}
      </div>

      {/* 新建知识卡片模态框 - 使用 NewNoteModal，enableAIWriter 由打开时参数控制 */}
      <NewNoteModal
        open={isCreateModalVisible}
        batchResult={batchResult}
        selectedFiles={selectedFiles}
        setSelectedFiles={setSelectedFiles}
        createNoteKnowledgeBase={createNoteKnowledgeBase}
        createAvailableKnowledgeBases={createAvailableKnowledgeBases}
        handleCreateNoteKnowledgeBaseChange={handleCreateNoteKnowledgeBaseChange}
        linkForm={linkForm}
        handleCreateFromLink={handleCreateFromLink}
        createForm={createForm}
        manualInputContent={manualInputContent}
        setManualInputContent={setManualInputContent}
        handleCreateNote={handleCreateNote}
        isBatchCreating={isBatchCreating}
        createNoteProtection={createNoteProtection}
        setIsCreateModalVisible={setIsCreateModalVisible}
        setIsBatchCreating={setIsBatchCreating}
        setIsCreatingNote={setIsCreatingNote}
        handleCreateModalCancel={handleCreateModalCancel}
        enableAIWriter={createModalEnableAIWriter}
      />

      {/* 创建知识卡片进度计时器模态框 */}
      <Modal
        open={isCreatingNote}
        footer={null}
        closable={false}
        width={400}
        centered
        maskClosable={false}
        title={
          <div style={{ textAlign: 'center' }}>
            <FileTextOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:creating_note', { defaultValue: 'Creating Note' })}</span>
          </div>
        }
        styles={{
          body: {
            padding: '32px',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '8px'
          }
        }}
      >
        <div style={{ fontSize: '16px', marginBottom: '24px' }}>
          🤖 {t('dashboard:ai_analyzing', { defaultValue: 'AI is performing comprehensive analysis of your knowledge card...' })}
        </div>
        <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '16px', fontFamily: 'monospace' }}>
          {Math.floor(createTimer / 60).toString().padStart(2, '0')}:
          {(createTimer % 60).toString().padStart(2, '0')}
        </div>
        <div style={{ fontSize: '14px', opacity: 0.9 }}>
          {t('dashboard:generating_summary', { defaultValue: 'Generating summary and tags. Please wait...' })}
        </div>
        <div style={{ marginTop: '24px' }}>
          <div style={{
            width: '100%',
            height: '4px',
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(90deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.6) 100%)',
              animation: 'loading-bar 2s ease-in-out infinite'
            }} />
          </div>
        </div>
      </Modal>

      {/* 新建主题模态框 */}
      <Modal
        open={isCreateTopicVisible}
        onCancel={handleCancelCreateTopic}
        footer={null}
        width={500}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlusOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:actions.create_topic', { defaultValue: 'New Topic' })}</span>
          </div>
        }
      >
        <Form
          form={createTopicForm}
          onFinish={async (values) => {
            if (createTopicProtection.protect()) {
              try {
                await handleCreateTopic(values);
              } finally {
                createTopicProtection.release();
              }
            }
          }}
        >
          <Row align="middle" gutter={0} style={{ width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left' }}>{t('dashboard:topic_name', { defaultValue: 'Topic Name' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: t('messages:please_enter_topic_name', { defaultValue: 'Please enter topic name' }) }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder={t('dashboard:enter_topic_name', { defaultValue: 'Enter topic name' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:topic_description', { defaultValue: 'Topic Description' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: t('messages:please_enter_topic_description', { defaultValue: 'Please enter topic description' }) },
                  { min: 20, message: t('messages:min_20_chars', { defaultValue: 'At least 20 characters' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea rows={3} placeholder={t('dashboard:enter_topic_description', { defaultValue: 'Enter topic description (min 20 chars)' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:tag_list', { defaultValue: 'Tag List' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="tagnamelist"
                rules={[
                  { required: true, message: t('messages:please_enter_tag_list', { defaultValue: 'Please enter tag list' }) },
                  { min: 20, message: t('messages:tag_list_min', { defaultValue: 'At least 20 characters, comma-separated' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea
                  rows={6.75}
                  placeholder={t('dashboard:enter_tag_list', { defaultValue: 'Enter comma-separated tags (min 20 chars)' })}
                />
              </Form.Item>

              {/* AI生成按钮单独放置 */}
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button
                  type="link"
                  icon={<RobotOutlined />}
                  loading={aiGenerating}
                  onClick={() => handleAIGenerateTags(createTopicForm)}
                  style={{
                    padding: '0 8px',
                    height: 'auto',
                    fontSize: '12px'
                  }}
                >
                  {t('dashboard:ai_generate', { defaultValue: 'AI Generate' })}
                </Button>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 {t('dashboard:create_level0_tip', { defaultValue: 'This creates a Level 0 topic under your private library.' })}
            </Text>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancelCreateTopic}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                className="gradient-button"
                disabled={createTopicProtection.isProtected}
                loading={createTopicProtection.isProtected}
              >
                {createTopicProtection.isProtected
                  ? t('dashboard:creating', { defaultValue: 'Creating...' })
                  : t('dashboard:actions.create_topic', { defaultValue: 'New Topic' })
                }
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 新建子主题模态框 */}
      <Modal
        open={isCreateSubTopicVisible}
        onCancel={handleCancelCreateSubTopic}
        footer={null}
        width={500}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlusOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:actions.create_subtopic', { defaultValue: 'New Subtopic' })}</span>
            {selectedParentTopic && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '14px' }}>
                - {t('dashboard:parent_topic', { defaultValue: 'Parent Topic' })}: {selectedParentTopic.name}
              </Text>
            )}
          </div>
        }
      >
        <Form
          form={createSubTopicForm}
          onFinish={async (values) => {
            if (createSubTopicProtection.protect()) {
              try {
                await handleCreateSubTopic(values, selectedParentTopic);
              } finally {
                createSubTopicProtection.release();
              }
            }
          }}
        >
          <Row align="middle" gutter={0} style={{ width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left' }}>{t('dashboard:subtopic_name', { defaultValue: 'Subtopic Name' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: t('messages:please_enter_subtopic_name', { defaultValue: 'Please enter subtopic name' }) }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder={t('dashboard:enter_subtopic_name', { defaultValue: 'Enter subtopic name' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:subtopic_description', { defaultValue: 'Subtopic Description' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: t('messages:please_enter_subtopic_description', { defaultValue: 'Please enter subtopic description' }) },
                  { min: 20, message: t('messages:min_20_chars', { defaultValue: 'At least 20 characters' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea rows={3} placeholder={t('dashboard:enter_subtopic_description', { defaultValue: 'Enter subtopic description (min 20 chars)' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:tag_list', { defaultValue: 'Tag List' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="tagnamelist"
                rules={[
                  { required: true, message: t('messages:please_enter_tag_list', { defaultValue: 'Please enter tag list' }) },
                  { min: 20, message: t('messages:tag_list_min', { defaultValue: 'At least 20 characters, comma-separated' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea
                  rows={6.75}
                  placeholder={t('dashboard:enter_tag_list', { defaultValue: 'Enter comma-separated tags (min 20 chars)' })}
                />
              </Form.Item>

              {/* AI生成按钮单独放置 */}
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button
                  type="link"
                  icon={<RobotOutlined />}
                  loading={aiGenerating}
                  onClick={() => handleAIGenerateTags(createSubTopicForm)}
                  style={{
                    padding: '0 8px',
                    height: 'auto',
                    fontSize: '12px'
                  }}
                >
                  {t('dashboard:ai_generate', { defaultValue: 'AI Generate' })}
                </Button>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 {t('dashboard:create_subtopic_under', { defaultValue: 'This subtopic will be created under "{{name}}"', name: selectedParentTopic?.name })}
            </Text>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancelCreateSubTopic}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                className="gradient-button"
                disabled={createSubTopicProtection.isProtected}
                loading={createSubTopicProtection.isProtected}
              >
                {createSubTopicProtection.isProtected
                  ? t('dashboard:creating', { defaultValue: 'Creating...' })
                  : t('dashboard:actions.create_subtopic', { defaultValue: 'New Subtopic' })
                }
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑主题模态框 */}
      <Modal
        open={isEditTopicVisible}
        onCancel={handleCancelEditTopic}
        footer={null}
        width={500}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <EditOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:actions.edit_topic', { defaultValue: 'Edit Topic' })}</span>
            {editingTopic && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '14px' }}>
                - {editingTopic.name}
              </Text>
            )}
          </div>
        }
      >
        <Form
          form={editTopicForm}
          onFinish={handleConfirmEditTopic}
        >
          <Row align="middle" gutter={0} style={{ width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left' }}>{t('dashboard:topic_name', { defaultValue: 'Topic Name' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="name"
                rules={[{ required: true, message: t('messages:please_enter_topic_name', { defaultValue: 'Please enter topic name' }) }]}
                style={{ marginBottom: 0 }}
              >
                <Input placeholder={t('dashboard:enter_topic_name', { defaultValue: 'Enter topic name' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:topic_description', { defaultValue: 'Topic Description' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="description"
                rules={[
                  { required: true, message: t('messages:please_enter_topic_description', { defaultValue: 'Please enter topic description' }) },
                  { min: 20, message: t('messages:min_20_chars', { defaultValue: 'At least 20 characters' }) },
                  { max: 500, message: t('messages:max_500_chars', { defaultValue: 'Max 500 characters' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea rows={3} placeholder={t('dashboard:enter_topic_description', { defaultValue: 'Enter topic description (min 20 chars)' })} />
              </Form.Item>
            </Col>
          </Row>

          <Row align="top" gutter={0} style={{ marginTop: 16, width: '100%' }}>
            <Col span={6}>
              <Text strong style={{ display: 'inline-block', width: '100%', textAlign: 'left', lineHeight: '32px' }}>{t('dashboard:tag_list', { defaultValue: 'Tag List' })}</Text>
            </Col>
            <Col span={18}>
              <Form.Item
                name="tagnamelist"
                rules={[
                  { required: true, message: t('messages:please_enter_tag_list', { defaultValue: 'Please enter tag list' }) },
                  { min: 20, message: t('messages:tag_list_min', { defaultValue: 'At least 20 characters, comma-separated' }) }
                ]}
                style={{ marginBottom: 8 }}
              >
                <TextArea
                  rows={6.75}
                  placeholder={t('dashboard:enter_tag_list', { defaultValue: 'Enter comma-separated tags (min 20 chars)' })}
                />
              </Form.Item>

              {/* AI生成按钮单独放置 */}
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button
                  type="link"
                  icon={<RobotOutlined />}
                  loading={aiGenerating}
                  onClick={() => handleAIGenerateTags(editTopicForm)}
                  style={{
                    padding: '0 8px',
                    height: 'auto',
                    fontSize: '12px'
                  }}
                >
                  {t('dashboard:ai_generate', { defaultValue: 'AI Generate' })}
                </Button>
              </div>
            </Col>
          </Row>

          <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              💡 {t('dashboard:edit_topic_tip', { defaultValue: 'You can modify the topic name, description and tags.' })}
            </Text>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCancelEditTopic}>{t('common:actions.cancel', { defaultValue: 'Cancel' })}</Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} className="gradient-button">
                {t('common:actions.save_changes', { defaultValue: 'Save Changes' })}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 创建协作库模态框 */}
      <Modal
        open={isCreateCollaborativeVisible}
        onCancel={() => setIsCreateCollaborativeVisible(false)}
        title={t('dashboard:create_collaborative_kb', { defaultValue: 'Create Collaborative Knowledge Base' })}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          onFinish={async (values) => {
            if (createCollaborativeProtection.protect()) {
              try {
                await handleCreateCollaborative(values);
              } finally {
                createCollaborativeProtection.release();
              }
            }
          }}
          style={{ marginTop: 8 }}
        >
          <div style={{ width: '85%', margin: '0 auto' }}>
            <Row align="middle" gutter={0} style={{ width: '100%', marginBottom: 16 }}>
              <Col span={6}>
                <div style={{ fontWeight: 500, textAlign: 'left' }}>
                  {t('dashboard:kb_name', { defaultValue: 'KB Name' })} <span style={{ color: '#ff4d4f' }}>*</span>
                </div>
              </Col>
              <Col span={18}>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: t('dashboard:please_enter_kb_name', { defaultValue: 'Please enter KB name' }) }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder={t('dashboard:please_enter_kb_name', { defaultValue: 'Please enter KB name' })} />
                </Form.Item>
              </Col>
            </Row>

            <Row align="top" gutter={0} style={{ width: '100%', marginBottom: 8 }}>
              <Col span={6}>
                <div style={{ fontWeight: 500, textAlign: 'left', lineHeight: '32px' }}>
                  {t('dashboard:description', { defaultValue: 'Description' })}
                </div>
              </Col>
              <Col span={18}>
                <Form.Item name="description" style={{ marginBottom: 0 }}>
                  <TextArea rows={3} placeholder={t('dashboard:please_enter_kb_description', { defaultValue: 'Please enter KB description (optional)' })} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Form.Item style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button onClick={() => setIsCreateCollaborativeVisible(false)}>
                {t('common:actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="gradient-button"
                disabled={createCollaborativeProtection.isProtected}
                loading={createCollaborativeProtection.isProtected}
              >
                {createCollaborativeProtection.isProtected
                  ? t('dashboard:creating', { defaultValue: 'Creating...' })
                  : t('dashboard:create', { defaultValue: 'Create' })
                }
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>



      {/* 协作库成员查看模态框 */}
      <Modal
        open={isMembersVisible}
        onCancel={() => setIsMembersVisible(false)}
        footer={null}
        width={600}
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TeamOutlined style={{ marginRight: 8, color: '#667eea' }} />
            <span>{t('dashboard:kb_members', { defaultValue: 'KB Members' })}</span>
            {selectedKBForMembers && (
              <Text type="secondary" style={{ marginLeft: 8, fontSize: '14px' }}>
                - {selectedKBForMembers.name}
              </Text>
            )}
          </div>
        }
      >
        {loadingMembers ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>{t('dashboard:loading_members', { defaultValue: 'Loading member information...' })}</div>
          </div>
        ) : (
          <div>
            {kbMembers && kbMembers.length > 0 ? (
              <List
                dataSource={kbMembers}
                renderItem={(member) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar icon={<UserOutlined />} />}
                      title={member.username || member.name}
                      description={
                        <div>
                          <div>{t('dashboard:role', { defaultValue: 'Role' })}: {member.isCreator ? t('dashboard:actions.members.owner', { defaultValue: 'Owner' }) : (member.role || t('dashboard:member', { defaultValue: 'Member' }))}</div>
                          <div>{t('dashboard:joined_at', { defaultValue: 'Joined at' })}: {(member.joinedAt || member.joined_at) ? new Date(member.joinedAt || member.joined_at).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }) : t('dashboard:unknown', { defaultValue: 'Unknown' })}</div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div>{t('dashboard:no_member_info', { defaultValue: 'No member information' })}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 协作库重命名模态框 - 已移除，使用自定义Modal */}
    </Sider>
  );
};

// Collaborative KB Item Component
const CollaborativeKBItem = ({
  kb,
  isExpanded,
  onToggleExpanded,
  onStartCreateCollaborativeKBTopic,
  onShareCollaborativeKB,
  onViewMembers,
  onRenameCollaborativeKB,
  onConfirmRenameCollaborativeKB,
  onCancelRenameCollaborativeKB,
  notesCount,
  renderTopicTree,
  collaborativeKBTopics,
  onLeaveCollaborativeKB,
  isCollaborativeKBCreator,
  onCollaborativeKBClick,
  expandedCollaborativeKBsArray,
  expandedTopics
}) => {
  const { t } = useTranslation(['dashboard', 'common']);
  return (
    <div style={{ width: '100%' }}>
      <div className={`folder-item ${getSelectedKB() && getSelectedKB().kb_id === kb.kb_id ? 'active' : ''}`} style={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 0,
        width: '100%',
        minHeight: '26px' /* 降低20%：从32px改为26px */
      }}>
        <div style={{
          width: '16px',
          display: 'flex',
          justifyContent: 'center',
          marginRight: '4px',
          flexShrink: 0
        }}>
          <Button
            type="text"
            size="small"
            icon={isExpanded ? <FolderOpenOutlined /> : <FolderOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded();
            }}
            style={{
              padding: 0,
              width: '16px',
              height: '16px',
              fontSize: '10px',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={isExpanded ? t('dashboard:collapse', { defaultValue: 'Collapse' }) : t('dashboard:expand', { defaultValue: 'Expand' })}
          />
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {/* Removed isEditing and editingName props */}
            <Tooltip
              title={
                <div>
                  <div><strong>{kb.name}</strong></div>
                  {kb.description && <div style={{ marginTop: 4, fontSize: '12px', opacity: 0.8 }}>{kb.description}</div>}
                </div>
              }
              placement="right"
            >
              <span
                className="folder-title"
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
                title={kb.name}
                onClick={() => {
                  console.log('[前端] 点击协作库标题:', kb.kb_id);
                  // 点击协作库标题时仅选中知识库；展开/折叠由前方 folder 图标点击控制
                  if (onCollaborativeKBClick) {
                    onCollaborativeKBClick(kb);
                  }
                }}
              >
                {kb.name}
              </span>
            </Tooltip>
          </div>

          {/* 笔记数量和菜单区域 - 右对齐 */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: '4px' }}>
            {/* 双人图标 - 标识协作库 */}
            <TeamOutlined
              style={{
                fontSize: '12px',
                color: '#9ca3af',
                flexShrink: 0
              }}
              title={t('dashboard:collaborative_kb', { defaultValue: 'Collaborative Library' })}
            />
            <span className="folder-count" style={{ fontSize: '12px', color: '#6b7280', textAlign: 'right', minWidth: '24px' }}>
              ({notesCount})
            </span>

            <Dropdown
            menu={{
              items: [
                {
                  key: 'share',
                  label: t('dashboard:actions.share', { defaultValue: 'Share' }),
                  icon: <ShareAltOutlined />,
                  onClick: () => {
                    console.log('[CollaborativeKBItem] 点击分享:', kb);
                    onShareCollaborativeKB(kb);
                  }
                },
                {
                  key: 'members',
                  label: t('dashboard:actions.members', { defaultValue: 'Members' }),
                  icon: <TeamOutlined />,
                  onClick: () => {
                    console.log('[CollaborativeKBItem] 点击成员:', kb);
                    onViewMembers(kb);
                  }
                },
                // 只有创建者才能重命名
                ...(isCollaborativeKBCreator ? [{
                  key: 'rename',
                  label: t('dashboard:actions.rename', { defaultValue: 'Rename' }),
                  icon: <EditOutlined />,
                  onClick: () => {
                    console.log('[CollaborativeKBItem] 点击重命名:', kb);
                    onRenameCollaborativeKB(kb);
                  }
                }] : []),
                {
                  key: 'leave-kb',
                  label: t('dashboard:actions.leave_kb', { defaultValue: 'Leave KB' }),
                  icon: <CloseOutlined />,
                  onClick: () => {
                    onLeaveCollaborativeKB(kb);
                  },
                  danger: true
                }
              ]
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              style={{
                padding: '0 4px',
                fontSize: '14px',
                height: '20px',
                marginLeft: '4px',
                fontWeight: 'bold',
                color: '#444444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: '1'
              }}
              className="collaborative-kb-menu-button"
            >
              <span style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.5px',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                lineHeight: '1'
              }}>
                <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
                <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
                <span style={{ display: 'block', width: '2px', height: '2px', borderRadius: '50%', backgroundColor: '#444444' }}></span>
              </span>
            </Button>
          </Dropdown>
          </div>
        </div>
      </div>

      {isExpanded && renderTopicTree && (
        <div key={`collaborative-topics-${kb.kb_id}-${expandedCollaborativeKBsArray?.length || 0}`} style={{ marginLeft: '2px' }}>
          {renderTopicTree(collaborativeKBTopics[kb.kb_id] || [], 1, { type: 'collaborative', kbId: kb.kb_id }, expandedTopics)}
        </div>
      )}
    </div>
  );
};

export default DashboardSidebar;